import DashboardCard from '@/components/shared/DashboardCard'
import { LoaderContainer } from '../Loader/LoaderContainer'
import Loader from '../Loader/Loader'
import { Fragment } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { formatMoney } from '@/util'
import ZeroState from '../ZeroState/ZeroState'
import { SkuImageName } from '../../../game-items/components/SkuImageName'
import { SKU_ITEMS_DETAILS_PATH } from '@/libs/routerPaths'
import { TopItemData } from '@/api/dashboard'
import { useGenerateCompanyPath } from '@/libs'
import { useTranslation } from 'react-i18next'
import { useTopSkuItemQuery } from '@/layouts/dashboard/api/useTopSkuItemQuery'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useLazyInView } from '@/libs/hooks/useLazyInView'
import { ChartProps, getQueryParams } from '@/layouts/dashboard/components/types'
import { useGameItemsQuery } from '@/api/useGameItems'
import { Divider } from '@/components'

export const TopSkuItems = ({ filters, style }: ChartProps) => {
  const { canEdit } = useCurrentUser()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { t } = useTranslation()
  const { ref, shouldLoad } = useLazyInView([filters.selectedFilter, gameId])
  const { data = [], isLoading } = useTopSkuItemQuery(
    companyId,
    gameId,
    getQueryParams(filters),
    shouldLoad,
    filters.demoSelectedFilter,
  )

  const { data: items = [] } = useGameItemsQuery(companyId, gameId, {
    ids: data.map(it => it.id).join(','),
  })

  const total = data.length ? data.reduce((acc, item) => acc + item.sales, 0) : 0

  const getItemCells = (item: TopItemData) => {
    if (!item) {
      return <></>
    }

    const baseItem = items.find(it => it.id === item.id)

    if (!canEdit || !baseItem) {
      return (
        <div className="flex w-full items-center gap-3">
          <SkuImageName item={item} hideQuantity />
        </div>
      )
    }

    return (
      <Link
        className="flex w-full items-center gap-3"
        to={generatePath(SKU_ITEMS_DETAILS_PATH, { companyId, gameId, itemId: item.id || '' })}
      >
        <SkuImageName item={baseItem || item} hideQuantity />
      </Link>
    )
  }

  return (
    <DashboardCard
      title={t('dashboard.sales.top-5-items')}
      tooltip={t('dashboard.sales.top-5-items.tooltip')}
      data-testid="dashboard/chart/top5-sku"
      ref={ref}
      style={{
        ...style,
        gap: '8px',
      }}
    >
      {isLoading ? (
        <LoaderContainer style={{ height: '460px' }}>
          <Loader />
        </LoaderContainer>
      ) : (
        <>
          <div
            data-testid="dashboard/chart/top5-sku/total"
            className="flex h-[52px] items-center text-title-t3 font-semibold text-text-secondary"
          >
            {formatMoney(total)}
          </div>
          {data.length > 0 ? (
            <div style={{ maxHeight: '332px' }}>
              {data.map((item, index) => (
                <Fragment key={item.id}>
                  <div className="flex h-[66px] items-center">
                    <div className="w-full truncate">{getItemCells(item)}</div>
                    <div className="flex text-right">
                      <div
                        className="ml-3 text-caption-sm font-normal text-text-tertiary"
                        data-testid={`dashboard/chart/top5-sku/${item.id}/count`}
                      >
                        {item.count_sales}
                      </div>
                      <div
                        className="ml-3 text-caption-sm font-normal text-text-primary"
                        data-testid={`dashboard/chart/top5-sku/${item.id}/sales`}
                      >
                        {formatMoney(item.sales)}
                      </div>
                    </div>
                  </div>
                  {index < data.length - 1 && <Divider className="my-0 ml-auto w-[calc(100%-38px-12px)]" />}
                </Fragment>
              ))}
            </div>
          ) : (
            <ZeroState style={{ height: '332px' }} />
          )}
        </>
      )}
    </DashboardCard>
  )
}
